import { AU_DIVIDER_SOFT, AU_SUCCESS, AU_TEXT_DIM } from '@/theme/au-compat';
import { Trans } from '@lingui/macro';
import { ArrowRightAlt } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { ReactNode, useMemo, type FC } from 'react';
import { PanelHeading, PanelPaper } from './common';
import { MovementSummaryAttribute as Renderer } from './dataComponents/MovementSummaryAttribute';

interface Props {
  movementId: number | null;
}

export const PanelSummary: FC<Props> = ({ movementId }) => {
  const fields = useMemo(() => {
    return [
      {
        id: 0,
        label: <Trans>Operación compuerta</Trans>,
        value: (
          <>
            <Renderer movementId={movementId} attribute="startPosition" />
            <ArrowRightAlt sx={{ fontSize: 14, color: AU_TEXT_DIM, flexShrink: 0 }} />
            {/* `--au-success`, la base del tono: va en negrita sobre texto normal y no necesita la
                variante aclarada. */}
            <Box component="span" sx={{ color: AU_SUCCESS }}>
              <Renderer movementId={movementId} attribute="requestedPosition" />
            </Box>
          </>
        ),
      },
      {
        id: 2,
        label: <Trans>Solicitado por</Trans>,
        value: <Renderer movementId={movementId} attribute="requestedBy" />,
      },
      {
        id: 3,
        label: <Trans>Fecha solicitud</Trans>,
        value: <Renderer movementId={movementId} attribute="requestedAt" />,
      },
      {
        id: 4,
        label: <Trans>Código de la operación</Trans>,
        value: <Renderer movementId={movementId} attribute="operationCode" />,
      },
      {
        id: 1,
        label: <Trans>Operación iniciada hace</Trans>,
        value: <Renderer movementId={movementId} attribute="startTimestamp" />,
      },
    ];
  }, [movementId]);

  return (
    // `flex: none` — el resumen es una lista de largo conocido y mide lo que mide su contenido; el
    // que se estira es el histórico, que gana con cada pixel.
    <PanelPaper sx={{ flex: 'none', minWidth: 0 }}>
      <PanelHeading>
        <Trans>
          Resumen <span style={{ whiteSpace: 'nowrap' }}>de la operación</span>
        </Trans>
      </PanelHeading>

      {/* `.oc-summary-fields` — una `<dl>` de pares rótulo/valor alineados a la izquierda,
          separados por línea. */}
      <Box
        component="dl"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          m: 0,
          // La línea separadora va en el padre y no como `& + &` en el campo: un `& + &` depende de
          // que los dos hermanos caigan en la misma clase de Emotion, que es cierto hoy porque el
          // `sx` es idéntico y se deduplica, y deja de serlo en cuanto un campo necesite una
          // variante. Acá el selector es estructural y no puede desincronizarse.
          '& > div + div': { borderTop: `1px solid ${AU_DIVIDER_SOFT}`, pt: '10px' },
        }}
      >
        {fields.map(({ id, ...props }) => (
          <Field key={id} {...props} />
        ))}
      </Box>
    </PanelPaper>
  );
};

/**
 * `.oc-summary-field` — el par rótulo/valor.
 *
 * El contorno que llevaba cada rótulo lo hacía parecer un **chip** —una etiqueta que clasifica—
 * cuando es el nombre de un campo. El peso lo lleva ahora el contraste entre el rótulo atenuado
 * (11px/400) y su valor (12,5px/700); la línea separadora la pone la lista, sólo entre campos, así
 * que el primero no la tiene y no queda un filo suelto contra el título del panel.
 */
const Field: FC<{ label: ReactNode; value: ReactNode }> = ({ label, value }) => {
  return (
    <Box sx={{ textAlign: 'left' }}>
      <Typography
        component="dt"
        sx={{ fontSize: 11, fontWeight: 400, color: AU_TEXT_DIM, lineHeight: 1.3, mb: '1px' }}
      >
        {label}
      </Typography>
      <Typography
        component="dd"
        sx={{
          fontSize: 12.5,
          fontWeight: 700,
          m: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};
